import type { Marker } from "@repo/ui";

import { dateOf, dateTimeOf, timeOf } from "@repo/helpers";

import type { LayerDefinition, PositionViewModel } from "../../models";

import { positionGroupKey } from "./hooks/queries/positionGrouping";
import { isLayerVisible } from "./layers/layer";
import { layerIconImage, layerItemColor } from "./layers/layerAppearance";
import { isPositionLayer } from "./layers/layerTypes";

export function showsPaths(layer: LayerDefinition) {
	return isLayerVisible(layer) && !!layer.showLines && isPositionLayer(layer.itemType);
}

// The item type is part of the look: Machines layers color and group their positions by machine.
export function pinMarkersSignature(layer: LayerDefinition) {
	return `${layer.color}|${layer.icon}|${layer.ordinal}|${layer.itemType}`;
}

export function pathMarkersSignature(layer: LayerDefinition) {
	return `${layer.color}|${layer.ordinal}|${layer.itemType}`;
}

export function buildPinMarkers(layer: LayerDefinition, items: PositionViewModel[]): Marker[] {
	const image = layerIconImage(layer.icon);
	return items.map((item) => {
		const date = new Date(item.date);
		return {
			id: `${layer.id}:${item.id}`,
			lat: item.lat,
			lon: item.lng,
			datetime: date,
			label: Object.entries(item.displayFields ?? {})
				.filter(([, value]) => value != null)
				.map(([key, value]) => ({ [key]: fillDatePlaceholder(value, date) })),
			tooltip: item.tooltip
				?.split(" - ")
				.map((part) => fillDatePlaceholder(part, date))
				.join(" - "),
			links: item.links,
			image,
			color: layerItemColor(layer, item),
			ordinal: layer.ordinal,
		};
	});
}

export function buildPathMarkers(layer: LayerDefinition, items: PositionViewModel[]): Marker[] {
	return items.map((item) => ({
		id: `${layer.id}:path:${item.id}`,
		lat: item.lat,
		lon: item.lng,
		datetime: new Date(item.date),
		group: `${layer.id}:${positionGroupKey(layer.itemType, item)}`,
		color: layerItemColor(layer, item),
		ordinal: layer.ordinal,
		label: [],
		image: "",
	}));
}

function fillDatePlaceholder(value: string, date: Date) {
	switch (value) {
		case "DATE_TIME":
			return dateTimeOf(date);
		case "DATE":
			return dateOf(date);
		case "TIME":
			return timeOf(date);
		default:
			return value;
	}
}
